import type { DepilawayDatabase } from '../database';
import { now } from '../../lib/uuid';

export interface BcvSyncResult {
  status: 'up_to_date' | 'success' | 'no_rates' | 'error';
  addedCount: number;
  message: string;
}

export interface BcvRateItem {
  id: string;
  since: string;
  until: string;
  usd: number;
  eur: number;
}

/**
 * Centralized Dynamic BCV Synchronization Engine (Server-Side Delegate).
 * 
 * Pipeline:
 * 1. Resolves configured start_year / cutoff date.
 * 2. Calls Serverless Cloudflare Function /api/bcv to perform server-side seeding & online delta sync directly to Supabase.
 * 3. Inserts clean synced records into RxDB with sync_state = 'synced' (Nube Badge).
 */
export async function performBcvSync(
  db: DepilawayDatabase,
  sourceLabel: string = 'api_bcv_update',
  fromDate?: string
): Promise<BcvSyncResult> {
  if (!db) {
    return { status: 'error', addedCount: 0, message: 'Base de datos no inicializada.' };
  }

  try {
    const timestamp = now();
    let addedCount = 0;

    let requestPayload: any = {};

    if (fromDate) {
      requestPayload = { start_year: Number(fromDate.substring(0, 4)) };
    } else {
      const latestLocalDoc = await db.exchange_rates
        .findOne({ selector: { is_deleted: false }, sort: [{ until: 'desc' }] })
        .exec();

      if (latestLocalDoc && latestLocalDoc.until) {
        requestPayload = { latest_date: latestLocalDoc.until, ultima_fecha: latestLocalDoc.until };
      } else {
        const startYearConfig = await db.app_config.findOne('config-sistema-start-year').exec();
        const startYear = startYearConfig?.value || String(new Date().getFullYear());
        requestPayload = { start_year: Number(startYear) };
      }
    }

    console.log(`🌐 [bcvSync] Invocando /api/bcv con payload:`, requestPayload);

    // ── Call Serverless Cloudflare Function /api/bcv with 15s timeout ──
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    let res: Response;
    try {
      res = await fetch('/api/bcv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (res.ok) {
      const data = await res.json();
      console.log(`📊 [bcvSync] Respuesta de /api/bcv recibida. ${data.rates?.length || 0} tasas retornadas.`);

      if (data.rates && Array.isArray(data.rates) && data.rates.length > 0) {
        // Fetch all existing rate IDs in 1 single bulk query for O(1) Set lookup
        const candidateIds = data.rates.map((r: any) => r.id || `bcv_${r.since}`);
        const existingDocs = await db.exchange_rates.find({
          selector: { id: { $in: candidateIds } }
        }).exec();
        const existingSet = new Set(existingDocs.map(d => d.id));

        const docsToInsert: any[] = [];
        for (const rate of data.rates) {
          const rateId = rate.id || `bcv_${rate.since}`;
          if (!existingSet.has(rateId)) {
            docsToInsert.push({
              id: rateId,
              since: rate.since,
              until: rate.until,
              eur: Number(rate.eur),
              usd: Number(rate.usd),
              updated_at: timestamp,
              is_deleted: false,
              last_modified_by: sourceLabel,
              
            });
          }
        }

        if (docsToInsert.length > 0) {
          console.log(`☁️ [bcvSync] Insertando localmente ${docsToInsert.length} tasas con status 'synced' (Insignia Nube)...`);
          const bulkRes = await db.exchange_rates.bulkInsert(docsToInsert);
          addedCount = docsToInsert.length - bulkRes.error.length;
          console.log(`✅ [bcvSync] Inserción completada. ${addedCount} tasas guardadas.`);
        }

        return {
          status: 'success',
          addedCount,
          message: `Sincronizadas ${addedCount} tasas desde la nube.`
        };
      }
      return { status: 'up_to_date', addedCount: 0, message: 'Tasas al día en la nube.' };
    }

    return { status: 'error', addedCount: 0, message: 'Error en la respuesta del servidor.' };
  } catch (err: any) {
    console.error('[bcvSync] Sync failed:', err);
    return { status: 'error', addedCount: 0, message: err.message || 'Error de conexión.' };
  }
}
